import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toIsoInstant, type ReservationSummary } from '@swi/contracts';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import * as api from '../api/endpoints';
import { isApiError } from '../api/problem';
import { PageHeader } from '../components/layout/PageHeader';
import { AvailabilityVerdict } from './NewReservationPage';
import {
  Button,
  Card,
  EmptyState,
  Pill,
  Skeleton,
  StateBadge,
  buttonClass,
} from '../components/ui/primitives';
import { useAuth } from '../lib/auth';
import { dateTime, duration, money, relative } from '../lib/format';

/**
 * Two queues, because change C02 split one decision into two.
 *
 * - **Ke potvrzení** — drafts the dispatcher (OWNER_STAFF) turns into an allocation
 *   (OP-03). For an airship that needs approval this produces PENDING_APPROVAL
 *   instead of CONFIRMED, which is why the button says so.
 * - **Ke schválení majitelem** — requests only the fleet owner (ADMIN) may decide
 *   (OP-05). A dispatcher sees the queue but cannot act on it; hiding it would make
 *   the split invisible and the "waiting" state unexplainable.
 *
 * Each row is re-checked against /availability so the decision is made against the
 * rules as they stand now, not as they stood when the request was raised.
 */
export function ApprovalsPage() {
  const queryClient = useQueryClient();
  const { hasRole } = useAuth();
  const isApprover = hasRole('ADMIN');

  const drafts = useQuery({
    queryKey: ['reservations', 'drafts'],
    queryFn: () => api.listReservations({ state: 'DRAFT', pageSize: 50 }),
  });

  const pending = useQuery({
    queryKey: ['reservations', 'pending-approval'],
    queryFn: () => api.listReservations({ state: 'PENDING_APPROVAL', pageSize: 50 }),
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['reservations'] });
    void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    void queryClient.invalidateQueries({ queryKey: ['availability'] });
  };

  return (
    <>
      <PageHeader
        title="Ke schválení"
        description="Koncepty čekající na potvrzení dispečerem a žádosti čekající na rozhodnutí majitele flotily."
        actions={
          <div className="flex gap-2">
            <Pill tone={(drafts.data?.total ?? 0) > 0 ? 'warn' : 'accent'}>
              {drafts.data ? `${String(drafts.data.total)} konceptů` : '…'}
            </Pill>
            <Pill tone={(pending.data?.total ?? 0) > 0 ? 'warn' : 'accent'}>
              {pending.data ? `${String(pending.data.total)} u majitele` : '…'}
            </Pill>
          </div>
        }
      />

      <Queue
        title="Ke schválení majitelem"
        hint={
          isApprover
            ? 'Slot je držený, dokud nerozhodneš nebo dokud neuplyne lhůta.'
            : 'Rozhodnout smí jen majitel flotily (ADMIN). Slot je do té doby držený.'
        }
        query={pending}
        empty="Žádná žádost nečeká na rozhodnutí majitele."
        render={(row) => (
          <PendingRow key={row.id} row={row} isApprover={isApprover} onDone={invalidate} />
        )}
      />

      <Queue
        title="Ke potvrzení dispečerem"
        hint="U vzducholodí, které vyžadují schválení, potvrzení vytvoří žádost pro majitele."
        query={drafts}
        empty="Žádný koncept nečeká na potvrzení."
        render={(row) => <DraftRow key={row.id} row={row} onDone={invalidate} />}
      />
    </>
  );
}

function Queue({
  title,
  hint,
  query,
  empty,
  render,
}: {
  title: string;
  hint: string;
  query: { isLoading: boolean; data?: { items: ReservationSummary[] } };
  empty: string;
  render: (row: ReservationSummary) => React.ReactNode;
}) {
  return (
    <section className="mb-8">
      <h2 className="mb-1 text-[15px] font-semibold">{title}</h2>
      <p className="mb-3 text-[12px] text-[var(--text-muted)]">{hint}</p>
      {query.isLoading ? (
        <div className="flex flex-col gap-3">
          {Array.from({ length: 2 }, (_, index) => (
            <Skeleton key={index} className="h-44" />
          ))}
        </div>
      ) : (query.data?.items.length ?? 0) === 0 ? (
        <EmptyState title="Fronta je prázdná" description={empty} />
      ) : (
        <div className="flex flex-col gap-3">{query.data?.items.map(render)}</div>
      )}
    </section>
  );
}

/** Availability verdict for one queued reservation, excluding the row itself. */
function useRowAvailability(row: ReservationSummary, scope: string) {
  return useQuery({
    queryKey: ['availability', scope, row.id],
    queryFn: () =>
      api.checkAvailability({
        airshipId: row.airshipId,
        from: toIsoInstant(row.startAt),
        to: toIsoInstant(row.endAt),
        passengers: row.passengers,
        excludeReservationId: row.id,
      }),
  });
}

function RowSummary({ row }: { row: ReservationSummary }) {
  return (
    <div className="min-w-56">
      <p className="mono text-[13px] font-semibold text-[var(--accent)]">
        {row.airshipRegistration}
      </p>
      <p className="text-[13px]">
        {row.airshipName} · {row.departureHubCode} → {row.arrivalHubCode}
      </p>
      <p className="mt-1 text-[12px] text-[var(--text-secondary)]">
        {dateTime(row.startAt)} · {duration(row.startAt, row.endAt)} · {row.passengers} pax
      </p>
      <p className="text-[12px] text-[var(--text-muted)]">
        {row.renterName} · {relative(row.startAt)} · {money(row.priceCzk)}
      </p>
      {row.requiresApproval && (
        <p className="mt-1 text-[11px] font-semibold text-[var(--state-pending-approval)]">
          Vyžaduje schválení majitelem
        </p>
      )}
    </div>
  );
}

function ruleToast(error: unknown, fallback: string) {
  if (isApiError(error)) {
    toast.error(error.humanMessage, { description: error.technicalDetail });
  } else {
    toast.error(fallback);
  }
}

/** OP-03 from the dispatcher's side. */
function DraftRow({ row, onDone }: { row: ReservationSummary; onDone: () => void }) {
  const availability = useRowAvailability(row, 'draft-queue');

  const confirm = useMutation({
    mutationFn: () => api.confirmReservation(row.id),
    onSuccess: (result) => {
      toast.success(
        result.state === 'PENDING_APPROVAL'
          ? `${row.airshipRegistration}: žádost odešla majiteli flotily`
          : `${row.airshipRegistration} potvrzeno`,
      );
      onDone();
    },
    onError: (error: unknown) => ruleToast(error, 'Potvrzení se nepovedlo'),
  });

  // Declining a draft is a cancellation (OP-04), not an OP-05 rejection — the
  // approval process has not started yet, so there is nothing to reject.
  const cancel = useMutation({
    mutationFn: () => api.cancelReservation(row.id, { reason: 'Zrušeno dispečerem flotily' }),
    onSuccess: () => {
      toast.success(`${row.airshipRegistration} zrušeno`);
      onDone();
    },
    onError: (error: unknown) => ruleToast(error, 'Zrušení se nepovedlo'),
  });

  return (
    <Card>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <RowSummary row={row} />
        <div className="min-w-72 flex-1">
          <AvailabilityVerdict
            loading={availability.isLoading}
            available={availability.data?.available}
            violations={availability.data?.violations ?? []}
            conflicts={availability.data?.conflicts ?? []}
            turnaroundMinutes={availability.data?.turnaroundMinutes ?? row.turnaroundMinutes}
          />
        </div>
        <div className="flex flex-col gap-2">
          <Button
            variant="primary"
            size="sm"
            loading={confirm.isPending}
            disabled={availability.data?.available === false}
            onClick={() => confirm.mutate()}
          >
            {row.requiresApproval ? 'Poslat ke schválení' : 'Potvrdit'}
          </Button>
          <Button
            variant="danger"
            size="sm"
            loading={cancel.isPending}
            onClick={() => cancel.mutate()}
          >
            Zrušit
          </Button>
          <Link to={`/reservations/${row.id}`} className={buttonClass('ghost', 'sm')}>
            Detail
          </Link>
        </div>
      </div>
    </Card>
  );
}

/** OP-05 from the fleet owner's side. */
function PendingRow({
  row,
  isApprover,
  onDone,
}: {
  row: ReservationSummary;
  isApprover: boolean;
  onDone: () => void;
}) {
  const availability = useRowAvailability(row, 'approval-queue');
  const [reason, setReason] = useState('');

  const approve = useMutation({
    mutationFn: () => api.approveReservation(row.id),
    onSuccess: () => {
      toast.success(`${row.airshipRegistration} schváleno a potvrzeno`);
      onDone();
    },
    onError: (error: unknown) => ruleToast(error, 'Schválení se nepovedlo'),
  });

  const reject = useMutation({
    mutationFn: () => api.rejectReservation(row.id, { reason: reason.trim() }),
    onSuccess: () => {
      toast.success(`${row.airshipRegistration} zamítnuto`);
      setReason('');
      onDone();
    },
    onError: (error: unknown) => ruleToast(error, 'Zamítnutí se nepovedlo'),
  });

  return (
    <Card>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <StateBadge state="PENDING_APPROVAL" className="mb-2" />
          <RowSummary row={row} />
          {row.approvalDeadlineAt && (
            <p className="mt-2 text-[12px] font-semibold text-[var(--state-expired)]">
              Lhůta {dateTime(row.approvalDeadlineAt)} · {relative(row.approvalDeadlineAt)}
            </p>
          )}
        </div>

        <div className="min-w-72 flex-1">
          <AvailabilityVerdict
            loading={availability.isLoading}
            available={availability.data?.available}
            violations={availability.data?.violations ?? []}
            conflicts={availability.data?.conflicts ?? []}
            turnaroundMinutes={availability.data?.turnaroundMinutes ?? row.turnaroundMinutes}
          />
        </div>

        <div className="flex w-56 flex-col gap-2">
          {/*
            A dispatcher sees the queue but gets no controls at all (REQ-17).
            Disabled-but-visible buttons would invite a click that can only end in
            a 403, and the reason the click is pointless would not be on screen.
          */}
          {isApprover ? (
            <>
              <Button
                variant="primary"
                size="sm"
                loading={approve.isPending}
                disabled={availability.data?.available === false}
                onClick={() => approve.mutate()}
              >
                Schválit
              </Button>
              <input
                className="w-full rounded-[var(--radius-sm)] border border-[var(--border)] bg-transparent px-2 py-1 text-[12px]"
                placeholder="Důvod zamítnutí"
                value={reason}
                onChange={(event) => setReason(event.target.value)}
              />
              <Button
                variant="danger"
                size="sm"
                loading={reject.isPending}
                disabled={reason.trim().length < 3}
                onClick={() => reject.mutate()}
              >
                Zamítnout
              </Button>
            </>
          ) : (
            <p className="text-[12px] text-[var(--text-muted)]">
              Rozhodnout smí jen <strong>majitel flotily</strong>. Do té doby slot drží
              tato žádost.
            </p>
          )}
          <Link to={`/reservations/${row.id}`} className={buttonClass('ghost', 'sm')}>
            Detail
          </Link>
        </div>
      </div>
    </Card>
  );
}
